import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useNavigate, useParams } from 'react-router-dom';
import { buildTiktokPath } from '@/tiktok-platform/constants/tiktokRoutes';
import { useTiktokShop } from '@/tiktok-platform/context/TiktokShopContext';
import { tiktokPlatformAPI } from '@/tiktok-platform/api/tiktokPlatformApi';
import { extractTiktokList, pickField } from '@/tiktok-platform/utils/tiktokResponse';
import {
  CHECK_STATUS_LABELS,
  INVOICE_STATUS_LABELS,
  NO_RETURN_SIGN_LABELS,
  RECEIVE_TYPE_LABELS,
  RETURN_ORDER_TAG_LABELS,
  STOCK_MODE_LABELS,
  displayValue,
  extractGoodsIdList,
  formatMoney,
  mapLabel,
  mergeIdenticalReturnGoods,
  normalizeImageUrl,
  pickAttrName,
  pickReturnReason,
  statusLabelOf,
} from '@/tiktok-platform/modules/aftersales/returnOrderDisplay';
import { fixedUi } from '@/i18n/fixedUi';
import { showToast } from '@/utils/toast';
import TiktokProductThumb from '@/tiktok-platform/modules/products/components/TiktokProductThumb';
import { showTiktokConfirm } from '@/tiktok-platform/components/TiktokConfirmDialog';
import '@/styles/tiktok-platform/tiktok-product-ops.css';


const RETURN_STATUS_KEYS = {
  '待处理': 'rsPending',
  '已拒绝': 'rsRejected',
  '待买家寄回': 'rsAwaitBuyer',
  '买家已寄出': 'rsBuyerShipped',
  '拒绝收货': 'rsRejectReceive',
  '退货成功': 'rsReturnSuccess',
  '已取消': 'rsCancelled',
  '已完成': 'rsCompleted',
  '已关闭': 'rsClosed',
  '申请中': 'rsApplying',
  '卖家已收货': 'rsSellerReceived',
  '已送达': 'rsDelivered',
  '待交接': 'rsAwaitHandover',
  '待TEMU转账': 'rsTemuTransfer',
  '待SHEIN转账': 'rsSheinTransfer',
};

const RETURN_ENUM_KEYS = {
  '是': 'yes',
  '否': 'no',
  'KOL退货单': 'tagKol',
  '丢件': 'tagLost',
  '不开票': 'invoiceNone',
  '待开票': 'invoicePending',
  '已申请发票': 'invoiceApplied',
  '开票成功': 'invoiceDone',
  '已对账': 'reconciled',
  '未对账': 'unreconciled',
  'TikTok自主备货': 'stockTiktokSelf',
  '备货到TikTok仓': 'stockTiktokTo',
  '不备货到TikTok仓': 'stockTiktokNot',
  'TEMU自主备货': 'stockTemuSelf',
  '备货到TEMU仓': 'stockTemuTo',
  '不备货到TEMU仓': 'stockTemuNot',
  'SHEIN自主备货': 'stockSheinSelf',
  '备货到SHEIN仓': 'stockSheinTo',
  '不备货到SHEIN仓': 'stockSheinNot',
  '默认': 'receiveDefault',
  '卖家收货': 'receiveSeller',
  '仓库收货': 'receiveWarehouse',
};

function rd(key, options) {
  return i18n.t(`returnDetail.${key}`, { ns: 'workbench', ...options });
}

function trStatus(raw) {
  if (raw == null || raw === '') return raw;
  const key = RETURN_STATUS_KEYS[raw];
  return key ? i18n.t(`returnList.${key}`, { ns: 'workbench' }) : raw;
}

function trEnum(raw) {
  if (raw == null || raw === '') return raw;
  const key = RETURN_ENUM_KEYS[raw];
  return key ? rd(key) : fixedUi(raw);
}

function statusModifier(code) {
  const label = statusLabelOf(code) || String(code || '');
  if (/完成|成功|已签收|已退款|已收货|已妥投/.test(label)) return 'success';
  if (/拒绝|取消|失败|关闭/.test(label)) return 'failed';
  if (/待|处理中|审核|运输|申请|交接|中转/.test(label)) return 'pending';
  return 'pending';
}

function extractDetailRecord(response, returnOrderNo) {
  if (!response) return null;
  const list = extractTiktokList(response);
  if (list.length) {
    const matched = list.find((row) => String(pickField(row, 'returnOrderNo', 'returnNo')) === String(returnOrderNo));
    return matched || list[0];
  }
  if (typeof response === 'object' && !Array.isArray(response)) {
    if (pickField(response, 'returnOrderNo', 'returnNo') || response.returnGoodsInfoList) {
      return response;
    }
    const info = response.info ?? response.data;
    if (Array.isArray(info) && info.length) {
      const matched = info.find((row) => String(pickField(row, 'returnOrderNo', 'returnNo')) === String(returnOrderNo));
      return matched || info[0];
    }
    if (info && typeof info === 'object') return info;
  }
  return null;
}

function hasRichDetail(record, response) {
  if (response?.hasDetail === true) return true;
  if (!record || typeof record !== 'object') return false;
  if (Array.isArray(record.returnGoodsInfoList)) return true;
  return Boolean(
    pickField(record, 'requestReturnTime', 'allocateTime', 'site', 'memberExpressNo', 'platformExpressNo'),
  );
}


function isVideoUrl(url) {
  return /\.(mp4|webm|mov|m4v|avi)(\?|$)/i.test(String(url || ''));
}

/** 收集退货图片/视频，兼容多种字段与字符串项，去重后全部展示 */
function extractReturnMediaList(goods) {
  const buckets = [
    goods?.returnImageList,
    goods?.returnImages,
    goods?.imageList,
    goods?.returnVideoList,
    goods?.videoList,
  ];
  const out = [];
  const seen = new Set();
  for (const bucket of buckets) {
    if (!Array.isArray(bucket)) continue;
    for (const item of bucket) {
      if (item == null || item === '') continue;
      let link = '';
      let type = 0;
      if (typeof item === 'string') {
        link = normalizeImageUrl(item);
        type = isVideoUrl(link) ? 2 : 1;
      } else if (typeof item === 'object') {
        link = normalizeImageUrl(pickField(item, 'link', 'url', 'imageUrl', 'videoUrl', 'src'));
        type = Number(pickField(item, 'type')) || (isVideoUrl(link) ? 2 : 1);
      }
      if (!link || seen.has(link)) continue;
      seen.add(link);
      out.push({ link, type: type === 2 ? 2 : 1 });
    }
  }
  return out;
}

/** 展示全部文档字段；空值也显示 —，避免漏字段 */
function KvTable({ rows }) {
  if (!rows.length) {
    return <p className="tko-return-detail__empty">{i18n.t('orderList.emptyData', { ns: 'workbench' })}</p>;
  }
  return (
    <table className="tko-return-detail__kv">
      <tbody>
        {rows.map((row) => (
          <tr key={row.label}>
            <th scope="row">{row.label}</th>
            <td>{displayValue(row.value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CollapsibleKv({ primaryRows, extraRows }) {
  const [expanded, setExpanded] = useState(false);
  const rows = expanded ? [...primaryRows, ...extraRows] : primaryRows;
  return (
    <div className="tko-return-detail__kv-fold">
      <KvTable rows={rows} />
      {extraRows.length > 0 ? (
        <button
          type="button"
          className="tko-return-detail__fold-btn"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? rd('collapse') : rd('expandMore')}
        </button>
      ) : null}
    </div>
  );
}

function MoneyBlock({ goods }) {
  const currency = pickField(goods, 'currency') || pickField(goods, 'saleCurrency') || '';
  const primaryRows = [
    { label: rd('costPrice'), value: formatMoney(currency, pickField(goods, 'costPrice')) },
    { label: rd('promoDiscount'), value: formatMoney(currency, pickField(goods, 'sellerCurrencyPromotionPrice')) },
    { label: rd('returnHandlingFee'), value: formatMoney(currency, pickField(goods, 'returnExpense')) },
    { label: rd('performanceFee'), value: formatMoney(currency, pickField(goods, 'performancePrice')) },
    { label: rd('estimateSpend'), value: formatMoney(currency, pickField(goods, 'estimateIncomeMoney')) },
  ];
  const extraRows = [
    { label: rd('goodsPrice'), value: formatMoney(currency, pickField(goods, 'sellerCurrencyPrice')) },
    { label: rd('storeDiscount'), value: formatMoney(currency, pickField(goods, 'sellerCurrencyStoreCouponPrice')) },
    { label: rd('campaignDiscount'), value: formatMoney(currency, pickField(goods, 'settleCurrencyPromotionPrice')) },
    { label: rd('commission'), value: formatMoney(currency, pickField(goods, 'estimateCommission')) },
    { label: rd('commissionTax'), value: formatMoney(currency, pickField(goods, 'commissionSaleTax')) },
    { label: rd('euVat'), value: formatMoney(currency, pickField(goods, 'sellerRealTax')) },
    { label: rd('estimateSpendTax'), value: formatMoney(currency, pickField(goods, 'estimateTaxIncomeMoney')) },
  ];
  return <CollapsibleKv primaryRows={primaryRows} extraRows={extraRows} />;
}

export default function ReturnOrderDetailPage() {
  const { t } = useTranslation('workbench');
  const navigate = useNavigate();
  const { returnOrderNo: rawNo } = useParams();
  const returnOrderNo = decodeURIComponent(rawNo || '').trim();
  const { activeShopId, loading: shopLoading } = useTiktokShop();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [signing, setSigning] = useState(false);

  const loadDetail = useCallback(async ({ forceLive = false } = {}) => {
    if (!activeShopId || !returnOrderNo) return;
    setLoading(true);
    setError('');
    try {
      if (!forceLive) {
        try {
          const cached = await tiktokPlatformAPI.getCachedReturnOrderDetail(activeShopId, returnOrderNo);
          const record = extractDetailRecord(cached, returnOrderNo);
          if (record && hasRichDetail(record, cached)) {
            setDetail(record);
            return;
          }
        } catch {
          /* 本地无完整详情再拉平台 */
        }
      }

      const live = await tiktokPlatformAPI.getReturnOrderDetails(activeShopId, {
        returnOrderNoList: [returnOrderNo],
      });
      const record = extractDetailRecord(live, returnOrderNo);
      if (!record) {
        throw new Error(t('returnDetail.detailMissing'));
      }
      setDetail(record);
    } catch (e) {
      if (forceLive) {
        try {
          const cached = await tiktokPlatformAPI.getCachedReturnOrderDetail(activeShopId, returnOrderNo);
          const record = extractDetailRecord(cached, returnOrderNo);
          if (record) {
            setDetail(record);
            setError('');
            showToast(e?.message || t('returnDetail.liveFailed'), 'warning');
            return;
          }
        } catch {
          /* ignore */
        }
      }
      setDetail(null);
      setError(e?.message || t('returnDetail.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [activeShopId, returnOrderNo, t]);

  useEffect(() => {
    if (shopLoading) return;
    if (!activeShopId) {
      setError(t('returnList.pickAuthorized', { platform: 'TikTok' }));
      return;
    }
    if (!returnOrderNo) {
      setError(t('returnDetail.invalidReturnNo'));
      return;
    }
    loadDetail({ forceLive: false });
  }, [activeShopId, returnOrderNo, shopLoading, loadDetail, t]);

  const statusCode = pickField(detail || {}, 'returnOrderStatus', 'status');
  const statusLabel = trStatus(statusLabelOf(statusCode)) || '—';
  const goodsList = useMemo(
    () => (Array.isArray(detail?.returnGoodsInfoList) ? detail.returnGoodsInfoList : []),
    [detail],
  );
  const mergedGoodsList = useMemo(() => mergeIdenticalReturnGoods(goodsList), [goodsList]);
  const goodsIdList = useMemo(() => extractGoodsIdList(detail), [detail]);

  const basicPrimaryRows = useMemo(() => {
    if (!detail) return [];
    return [
      { label: t('returnList.returnNo'), value: pickField(detail, 'returnOrderNo', 'returnNo') },
      { label: t('returnDetail.relatedBill'), value: pickField(detail, 'orderNo', 'billNo') },
      { label: t('returnDetail.returnStatus'), value: statusLabel },
      {
        label: t('returnDetail.isReturn'),
        value: trEnum(mapLabel(NO_RETURN_SIGN_LABELS, pickField(detail, 'noReturnGoodsSign'))),
      },
    ];
  }, [detail, statusLabel, t]);

  const basicExtraRows = useMemo(() => {
    if (!detail) return [];
    return [
      { label: t('returnDetail.salesSite'), value: pickField(detail, 'site') },
      {
        label: t('returnDetail.returnTags'),
        value: trEnum(mapLabel(RETURN_ORDER_TAG_LABELS, pickField(detail, 'returnOrderTagCode'))),
      },
      { label: rd('performanceFee'), value: pickField(detail, 'performanceCost') },
      {
        label: t('returnDetail.invoiceStatus'),
        value: trEnum(mapLabel(INVOICE_STATUS_LABELS, pickField(detail, 'invoiceStatus'))),
      },
      {
        label: t('returnDetail.reconcileStatus'),
        value: trEnum(mapLabel(CHECK_STATUS_LABELS, pickField(detail, 'checkStatus'))),
      },
      {
        label: t('returnDetail.stockMode'),
        value: trEnum(mapLabel(STOCK_MODE_LABELS, pickField(detail, 'stockMode'))),
      },
      {
        label: t('returnDetail.receiveType'),
        value: trEnum(mapLabel(RECEIVE_TYPE_LABELS, pickField(detail, 'receiveType'))),
      },
      {
        label: t('returnDetail.platformReturnNo'),
        value: Array.isArray(detail.refundOrderNos)
          ? detail.refundOrderNos.join('、')
          : pickField(detail, 'refundOrderNos'),
      },
    ];
  }, [detail, t]);

  const logisticsRows = useMemo(() => {
    if (!detail) return [];
    return [
      { label: t('returnDetail.shippingCode'), value: pickField(detail, 'shippingCode') },
      { label: t('returnDetail.platformWaybill', { platform: 'TikTok' }), value: pickField(detail, 'platformExpressNo') },
      { label: t('returnDetail.buyerWaybill'), value: pickField(detail, 'memberExpressNo') },
      { label: t('returnDetail.returnCarrier'), value: pickField(detail, 'expressCompanyName') },
      { label: t('returnDetail.platformReturnWaybill'), value: pickField(detail, 'refundWaybill') },
      { label: t('returnDetail.platformReturnCarrier'), value: pickField(detail, 'refundExpressCompanyName') },
    ];
  }, [detail, t]);

  const timePrimaryRows = useMemo(() => {
    if (!detail) return [];
    return [
      { label: t('returnDetail.applyTime'), value: pickField(detail, 'requestReturnTime', 'applyTime') },
      { label: t('returnDetail.signTime'), value: pickField(detail, 'sellerSignedTime') },
      { label: t('returnDetail.cancelApplyTime'), value: pickField(detail, 'cancelTime') },
      { label: t('returnDetail.completeTime'), value: pickField(detail, 'completedTime') },
    ];
  }, [detail, t]);

  const timeExtraRows = useMemo(() => {
    if (!detail) return [];
    return [
      { label: t('returnDetail.allocateTime'), value: pickField(detail, 'allocateTime') },
      { label: t('returnDetail.updatedAt'), value: pickField(detail, 'lastUpdateTime') },
    ];
  }, [detail, t]);

  const handleBack = () => {
    navigate(buildTiktokPath('orders/returns'));
  };

  const handleSign = async () => {
    if (!activeShopId || !returnOrderNo) return;
    if (!goodsIdList.length) {
      showToast(t('returnDetail.missingGoodsId'), 'error');
      return;
    }
    const ok = await showTiktokConfirm({
      title: t('returnList.confirmSign'),
      message: t('returnList.signOne', { no: returnOrderNo, platform: 'TikTok' }),
      tone: 'warning',
      confirmText: t('returnList.confirmSign'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setSigning(true);
    try {
      await tiktokPlatformAPI.signReturnOrder(activeShopId, {
        returnOrderNo,
        goodsIdList,
      });
      showToast(t('returnList.signSubmitted'), 'success');
      await loadDetail({ forceLive: true });
    } catch (e) {
      showToast(e?.message || t('returnList.signFailed'), 'error');
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="tko-return-detail" data-testid="tiktok-return-order-detail">
      <nav className="tko-return-detail__crumb" aria-label={t('orderList.crumb')}>
        <button type="button" className="tko-return-detail__crumb-link" onClick={handleBack}>
          {t('platformNav.returns')}
        </button>
        <span className="tko-return-detail__crumb-sep">/</span>
        <span>{t('returnDetail.detailTitle')}</span>
      </nav>

      <header className="tko-return-detail__header">
        <div className="tko-return-detail__title-block">
          <h1 className="tko-return-detail__title">{t('returnDetail.detailTitle')}</h1>
          <div className="tko-return-detail__meta">
            <strong className="tko-return-detail__no">{returnOrderNo || '—'}</strong>
            {detail ? (
              <span className={`tko-records__status tko-records__status--${statusModifier(statusCode)}`}>
                {statusLabel}
              </span>
            ) : null}
          </div>
        </div>
        <div className="tko-return-detail__actions">
          <button type="button" className="tko-btn tko-btn--secondary tko-btn--sm" onClick={handleBack}>
            {t('orderList.backToList')}
          </button>
          <button
            type="button"
            className="tko-btn tko-btn--primary tko-btn--sm"
            disabled={!activeShopId || !detail || signing || !goodsIdList.length}
            onClick={handleSign}
          >
            {signing ? t('returnList.submitting') : t('returnList.confirmSign')}
          </button>
        </div>
      </header>

      {error ? (
        <div className="tko-ops-error" role="alert">{error}</div>
      ) : null}

      {loading && !detail ? (
        <div className="tko-return-detail__loading">{t('returnDetail.loadingDetail')}</div>
      ) : null}

      {!loading && !error && !detail ? (
        <div className="tko-return-detail__loading">{t('returnDetail.noDetail')}</div>
      ) : null}

      {detail ? (
        <div className="tko-return-detail__body">
          <section className="tko-return-detail__section">
            <h2 className="tko-return-detail__section-title">{t('orderList.basic')}</h2>
            <CollapsibleKv primaryRows={basicPrimaryRows} extraRows={basicExtraRows} />
          </section>

          <section className="tko-return-detail__section">
            <h2 className="tko-return-detail__section-title">{t('returnDetail.logistics')}</h2>
            <KvTable rows={logisticsRows} />
          </section>

          <section className="tko-return-detail__section">
            <h2 className="tko-return-detail__section-title">{t('returnDetail.timeInfo')}</h2>
            <CollapsibleKv primaryRows={timePrimaryRows} extraRows={timeExtraRows} />
          </section>

          <section className="tko-return-detail__section tko-return-detail__section--goods">
            <h2 className="tko-return-detail__section-title">
              {t('returnDetail.returnGoods')}
              <span className="tko-return-detail__section-count">{t('returnDetail.goodsCount', { count: goodsList.length })}</span>
            </h2>
            {goodsList.length === 0 ? (
              <p className="tko-return-detail__empty">{t('returnDetail.noGoods')}</p>
            ) : (
              <div className="tko-return-detail__goods-list">
                {mergedGoodsList.map((group, index) => {
                  const goods = group.goods;
                  const quantity = group.quantity;
                  const img = normalizeImageUrl(pickField(goods, 'imageUrl'));
                  const title = pickField(goods, 'goodsTitle') || '—';
                  const reason = pickReturnReason(goods.returnReasonList);
                  const attr = pickAttrName(goods.commodityAttributeList);
                  const gStatus = pickField(goods, 'goodsStatus');
                  const currency = pickField(goods, 'currency') || '';
                  const mediaList = extractReturnMediaList(goods);
                  return (
                    <article
                      key={`${pickField(goods, 'skc') || ''}-${pickField(goods, 'sku') || ''}-${index}`}
                      className="tko-return-detail__goods-card"
                    >
                      <div className="tko-return-detail__goods-head">
                        {img ? (
                          <TiktokProductThumb
                            imageUrl={img}
                            className="tko-return-detail__goods-img tko-return-detail__goods-img--lg"
                            placeholderClassName="tko-return-detail__goods-img tko-return-detail__goods-img--lg tko-return-detail__goods-img--empty"
                            alt={title}
                          />
                        ) : (
                          <div className="tko-return-detail__goods-img tko-return-detail__goods-img--lg tko-return-detail__goods-img--empty" />
                        )}
                        <div className="tko-return-detail__goods-head-meta">
                          <h3 className="tko-return-detail__goods-title">
                            {title}
                            {quantity > 1 ? (
                              <span className="tko-return-detail__goods-qty">×{quantity}</span>
                            ) : null}
                          </h3>
                          <div className="tko-return-detail__goods-tags">
                            <span className={`tko-records__status tko-records__status--${statusModifier(gStatus)}`}>
                              {trStatus(statusLabelOf(gStatus)) || '—'}
                            </span>
                            {quantity > 1 ? (
                              <span className="tko-return-detail__chip">{t('returnDetail.qtyChip', { count: quantity })}</span>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <div className="tko-return-detail__goods-reason">
                        <span className="tko-return-detail__goods-reason-label">{t('returnList.reason')}</span>
                        <span className="tko-return-detail__goods-reason-value">{reason || '—'}</span>
                      </div>

                      <div className="tko-return-detail__goods-grid">
                        <CollapsibleKv
                          primaryRows={[
                            { label: t('tiktokCatalog.productId'), value: pickField(goods, 'productId', 'product_id') },
                            { label: 'SKU ID', value: pickField(goods, 'sku', 'skuId') },
                            { label: t('returnDetail.merchantSku'), value: pickField(goods, 'skuSn') },
                          ]}
                          extraRows={[
                            { label: t('returnDetail.salesAttr'), value: attr },
                            { label: t('returnDetail.currency'), value: currency },
                            { label: t('returnDetail.saleCurrency'), value: pickField(goods, 'saleCurrency') },
                          ]}
                        />
                        <div>
                          <h4 className="tko-return-detail__subhead">
                            {t('returnDetail.amountDetail')}
                            {quantity > 1 ? (
                              <span className="tko-return-detail__section-count">{t('returnDetail.qtyTotal', { count: quantity })}</span>
                            ) : null}
                          </h4>
                          <MoneyBlock goods={goods} />
                        </div>
                      </div>

                      {mediaList.length ? (
                        <div className="tko-return-detail__return-media">
                          <h4 className="tko-return-detail__subhead">
                            {t('returnDetail.returnMedia')}
                            <span className="tko-return-detail__section-count">{mediaList.length}</span>
                          </h4>
                          <div className="tko-return-detail__media-list">
                            {mediaList.map((media, mediaIndex) => {
                              if (media.type === 2) {
                                return (
                                  <div key={`${media.link}-${mediaIndex}`} className="tko-return-detail__media-item tko-return-detail__media-item--video">
                                    <video
                                      className="tko-return-detail__media-video"
                                      src={media.link}
                                      controls
                                      preload="metadata"
                                    />
                                    <a
                                      className="tko-return-detail__media-link"
                                      href={media.link}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      {t('returnDetail.videoN', { index: mediaIndex + 1 })}
                                    </a>
                                  </div>
                                );
                              }
                              return (
                                <TiktokProductThumb
                                  key={`${media.link}-${mediaIndex}`}
                                  imageUrl={media.link}
                                  className="tko-return-detail__media-img"
                                  placeholderClassName="tko-return-detail__media-img"
                                  alt={t('returnDetail.mediaAlt', { index: mediaIndex + 1 })}
                                />
                              );
                            })}
                          </div>
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      ) : null}
    </div>
  );
}
